import { useState } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import { Link } from "react-router-dom";
import helpers from "../utils/helpers";
import fallBackImage from '../Assests/images/placeholderimage.jpg'
import { applicationService } from "../services/applicationService";
import DeleteModal from "./DeleteModal";
import toast from "react-hot-toast";

// ---------- Students Table Component ----------
export default function ApplicationDetailStudentsTable({ userRole, students, loader, decryptId, getApplicationDetail }) {
  const [selected, setSelected] = useState("");
  const [selectAll, setSelectAll] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteLoader, setDeleteLoader] = useState(false)

  const getProgressColor = (value) => {
    if (value >= 90) return "bg-green-500";
    if (value >= 70) return "bg-orange-400";
    return "bg-blue-600";
  };

  const handleSelectAll = (e) => {
    const checked = e.target.checked;
    setSelectAll(checked);
    if (checked) {
      const allIds = students.map((item) => item?.user_id).join(",");
      setSelected(allIds);
    } else {
      setSelected("");
    }
  };

  // Handle row select
  const handleSelect = (id) => {
    if (selected.includes(id)) {
      const currentIds = selected ? selected.split(",") : [];
      const newIdsArray = currentIds.filter((i) => i !== id.toString());
      const newIds = newIdsArray.join(",");
      setSelected(newIds);
      setSelectAll(false);
    } else {
      const currentIds = selected ? selected.split(",") : [];
      const newIdsArray = [...currentIds, id.toString()];
      const newIds = newIdsArray.join(",");
      setSelected(newIds);

      if (newIdsArray.length === students.length) {
        setSelectAll(true);
      }
    }
  };

  const handleDelete = async () => {
    try {
      setDeleteLoader(true)
      let data = JSON.stringify({
        'applicationId': decryptId,
        'educator_id': null,
        'student_id': selected,
      })
      let response = await applicationService.removeEducatorAndStudentFromAppDetails(data)
      if (response?.success == true) {
        setDeleteLoader(false)
        setIsDeleteOpen(false)
        toast.success(response?.message || 'Student deleted successfully')
        await getApplicationDetail()
        setSelected('')
        setSelectAll(false)
      } else {
        setDeleteLoader(false)
        setSelected('')
        setSelectAll(false)
        setIsDeleteOpen(false)
      }
    } catch (e) {
      setDeleteLoader(false)
      setSelected('')
      setSelectAll(false)
      setIsDeleteOpen(false)
      toast.error(e.response.data.message || 'Something went wrong')
    }
  }

  return (
    <div className="p-6 bg-white rounded-2xl shadow-md relative">
      <div className="flex items-center justify-end mb-4">
        {/* Show delete icon only if something is selected */}
        {(userRole?.role_name == 'Admin' && selected.length > 0) && (
          <button className="flex items-center gap-1 text-red-600 hover:text-red-800" onClick={() => setIsDeleteOpen(true)}>
            <TrashIcon className="w-6 h-6" />
            Delete
          </button>
        )}
      </div>

      <div className="overflow-x-auto h-[230px] scrollbar-hide">
        <table className="min-w-full border-separate border-spacing-y-3">
          <thead className="sticky top-0 bg-white">
            <tr className="text-gray-500 text-sm">
              <th className="pl-3 py-3 text-left align-middle">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAll}
                  className="w-4 h-4"
                />
              </th>
              <th className="text-left py-3">STUDENT</th>
              <th className="text-left py-3">PROGRESS</th>
              <th className="text-left py-3">LAST LOGIN</th>
              <th className="text-left py-3">STATUS</th>
              {userRole?.role_name == 'Admin' && <th className="text-left py-3">ACTION</th>}
            </tr>
          </thead>
          <tbody>
            {loader ?
              <tr>
                <td colSpan={userRole?.role_name == 'Admin' ? 6 : 5}>
                  <div className="h-[150px] flex items-center justify-center">
                    <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
                  </div>
                </td>
              </tr>
              :
              (students && students.length > 0) ? students.map((student, index) => (
                <tr
                  key={index}
                  className="bg-white rounded-xl shadow-sm hover:shadow-md transition"
                >
                  {/* Checkbox */}
                  <td className="py-3 pl-3 align-middle">
                    <input
                      type="checkbox"
                      checked={selected.includes(student?.user_id)}
                      onChange={() => handleSelect(student?.user_id)}
                      className="w-4 h-4"
                    />
                  </td>

                  {/* Student Info */}
                  <td className="py-3 align-middle">
                    <div className="flex items-center gap-3">
                      <img
                        src={student?.profile_pic || fallBackImage}
                        alt={student?.full_name}
                        className="w-10 h-10 rounded-full"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = fallBackImage;
                        }}
                      />
                      <div>
                        <p className="font-medium">{student?.full_name}</p>
                        <p className="text-sm text-gray-400">ID: {student?.user_id}</p>
                      </div>
                    </div>
                  </td>

                  {/* Progress */}
                  <td className="py-3 align-middle">
                    <div className="flex items-center gap-2 w-40">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`${getProgressColor(
                            student?.score
                          )} h-2 rounded-full`}
                          style={{ width: `${student?.score}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-600">
                        {student?.score}%
                      </span>
                    </div>
                  </td>

                  {/* Last Login */}
                  <td className="py-3 text-gray-500 align-middle">
                    {student?.launch_date}
                  </td>

                  {/* Status */}
                  <td className="py-3 align-middle">
                    <span className={`flex items-center gap-2 ${student?.is_active == 1 ? 'text-green-600' : 'text-red-600'}`}>
                      <span className={`w-2 h-2 ${student?.is_active == 1 ? 'bg-green-500' : 'bg-red-500'} rounded-full`}></span>
                      {student?.is_active == 1 ? 'Active' : 'In-active'}
                    </span>
                  </td>

                  {/* Action */}
                  {userRole?.role_name == 'Admin' && <td className="py-3 align-middle">
                    <Link to={`/userProfile/${helpers.encryptId(student?.user_id)}`}><button className="text-blue-600 hover:underline">
                      View Details
                    </button>
                    </Link>
                  </td>
                  }
                </tr>
              ))
                :
                <tr className="text-center h-[150px] content-center">
                  <td colSpan={userRole?.role_name == 'Admin' ? 6 : 5} className="text-gray-400 text-sl">There are currently no student.</td>
                </tr>
            }
          </tbody>
        </table>
      </div>
      <DeleteModal
        isOpen={isDeleteOpen}
        onClose={() => { setIsDeleteOpen(false); setSelected(''); setSelectAll(false) }}
        onConfirm={handleDelete}
        title='Delete'
        deleteLoader={deleteLoader}
      />
    </div>
  );
}
